import React, { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API } from "@/lib/api";

/**
 * Popover-driven CSV export with period presets + custom date range.
 * Usable anywhere the current user is allowed to export (admin OR can_export).
 */
export default function ReportExportMenu({ triggerLabel = "Export CSV", testid = "download-report" }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);

  const downloadReport = async (params = {}) => {
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const url = `${API}/admin/report${qs.toString() ? `?${qs.toString()}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const tag = params.date_from || params.date_to
        ? `${params.date_from || "start"}_to_${params.date_to || "today"}`
        : String(Date.now());
      a.download = `shipments_report_${tag}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Report downloaded");
    } catch {
      toast.error("Report download failed");
    }
  };

  const preset = (kind) => {
    const d = new Date();
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() - d.getDay());
    const startOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    const startOfQuarter = new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const map = {
      all: { date_from: "", date_to: "" },
      week: { date_from: iso(startOfWeek), date_to: iso(today) },
      month: { date_from: iso(startOfMonth), date_to: iso(today) },
      quarter: { date_from: iso(startOfQuarter), date_to: iso(today) },
      year: { date_from: iso(startOfYear), date_to: iso(today) },
    };
    downloadReport(map[kind]);
  };

  const submitCustom = () => {
    if (!from && !to) return toast.error("Pick at least one date or use a preset");
    downloadReport({ date_from: from, date_to: to });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testid} className="bg-blue-700 hover:bg-blue-800 text-white">
          <Download className="w-4 h-4 mr-1.5" /> {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">
              Quick periods
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => preset("week")} data-testid="report-preset-week">This week</Button>
              <Button size="sm" variant="outline" onClick={() => preset("month")} data-testid="report-preset-month">This month</Button>
              <Button size="sm" variant="outline" onClick={() => preset("quarter")} data-testid="report-preset-quarter">This quarter</Button>
              <Button size="sm" variant="outline" onClick={() => preset("year")} data-testid="report-preset-year">This year</Button>
              <Button size="sm" variant="outline" onClick={() => preset("all")} className="col-span-2" data-testid="report-preset-all">All time</Button>
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Custom range</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 mt-1 text-xs" data-testid="report-from" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">To</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 mt-1 text-xs" data-testid="report-to" />
              </div>
            </div>
            <Button size="sm" onClick={submitCustom} className="w-full mt-2 bg-blue-700 hover:bg-blue-800 text-white" data-testid="report-custom-download">
              Download custom range
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
